// M3 price regime (M3_SPEC §9.1-§9.2, §9.4 short-haul premium): a multiplier on spot for oil sold from a field.
//   US fields (US_PRICE_REGIONS): import-quota premium 1946 → Mar 1959 → 1972 (usPriceMult = EIA first purchase /
//     Arabian Light), then the 1971-81 control tiers per well (old / new / stripper), blended by today's rates.
//   Concession regions (POSTED_PRICE_REGIONS): cargoes realize posted × (1 − realizedDiscount) (the 1957-70 glut).
//   West-of-Suez crude fetches a short-haul premium while the canal is shut.
// Used by economy/market.js netPriceMult, so this file imports data modules only (no sim imports: no import cycle).
import { FIELDS } from '../../data/fields.js';
import { yearFrac } from '../../core/time.js';
import { wellsOnField } from '../indexes.js';
import {
  M3_FROM, usPriceMult, usPriceCeiling, US_PRICE_CONTROLS, US_PRICE_REGIONS, realizedDiscount, POSTED_PRICE_REGIONS,
  suezOpen, SHORT_HAUL_PREMIUM,
} from '../../data/m3/rules.js';

const US = new Set(US_PRICE_REGIONS);
const POSTED = new Set(POSTED_PRICE_REGIONS);
const SHORT = new Set(SHORT_HAUL_PREMIUM.regionIds);

const M4_US = new Set(['north_dakota', 'colorado', 'louisiana']); // M4-B: US regions added in M4 (gated; M4_SPEC §18.11)
export const isUsRegion = (rid) => US.has(rid) || M4_US.has(rid);
export const isPostedRegion = (rid) => POSTED.has(rid);
export const controlsActive = (y) => y >= US_PRICE_CONTROLS.from && y < US_PRICE_CONTROLS.until;

// Year a well first produced (ops sets well.firstProdYear at completion too; this is the fallback).
export function wellFirstProdYear(well) {
  if (well.firstProdYear != null) return well.firstProdYear;
  const d = well.prod?.startDay;
  return d != null ? Math.floor(yearFrac(d)) : null;
}

// Control tier of a US well at year y: 'old' | 'new' | 'stripper' (null outside 1971.62-1981.08).
export function usTier(well, y) {
  if (!controlsActive(y)) return null;
  const bbld = well.prod?.rate ?? 0;
  if (bbld > 0 && bbld <= US_PRICE_CONTROLS.stripperBbld) return 'stripper';
  const fp = wellFirstProdYear(well) ?? Math.floor(y);
  return fp > US_PRICE_CONTROLS.newAfterYear ? 'new' : 'old';
}

// Price of one US well's barrel as a multiple of spot (spot > 0).
export function usWellMult(well, y, spot) {
  const quota = usPriceMult(y);
  if (!controlsActive(y)) return quota;
  const free = Math.max(1, quota);                     // under controls, uncontrolled tiers fetch at least world parity
  const bbld = well.prod?.rate ?? 0;
  const ceil = usPriceCeiling(y, { firstProdYear: wellFirstProdYear(well) ?? Math.floor(y), bbld: bbld > 0 ? bbld : 100 });
  return ceil == null ? free : Math.min(free, ceil / spot);
}

// usWellMult is pure in (y, spot, rate, first-production year): memoized per well object (not in the state).
const WELL_MULT = new WeakMap();
function usWellMultMemo(w, y, spot) {
  const rate = w.prod?.rate, fp = wellFirstProdYear(w);
  const m = WELL_MULT.get(w);
  if (m && m.y === y && m.spot === spot && m.rate === rate && m.fp === fp) return m.v;
  const v = usWellMult(w, y, spot);
  WELL_MULT.set(w, { y, spot, rate, fp, v });
  return v;
}

// Rate-weighted blend over the player's producing wells in the field. Not cached: rates change during the day, and a
// cache would make a reloaded save diverge from the running game.
function usFieldMult(state, fieldId, y, spot) {
  let a = 0, b = 0;
  for (const w of wellsOnField(state, fieldId)) { // perf: per-day field index (same order as Object.values)
    if (!w.prod || w.ownerOperatorId) continue;
    const q = w.prod.rate > 0 ? w.prod.rate : 0;
    if (!(q > 0)) continue;
    a += q * usWellMultMemo(w, y, spot); b += q;
  }
  return b > 0 ? a / b : (controlsActive(y) ? Math.max(1, usPriceMult(y)) : usPriceMult(y));
}

// Multiplier on spot (1 before 1946 and for every region the M3 regime does not touch).
export function m3PriceMult(state, fieldId) {
  const y = yearFrac(state.day);
  if (y < M3_FROM) return 1;
  const rid = FIELDS[fieldId]?.regionId;
  if (!rid) return 1;
  const spot = state.market?.spot;
  let m = 1;
  if (US.has(rid)) {
    if (spot > 0) m *= usFieldMult(state, fieldId, y, spot);
  } else if (POSTED.has(rid)) m *= 1 - realizedDiscount(y);
  if (SHORT.has(rid) && !suezOpen(y)) m *= SHORT_HAUL_PREMIUM.mult;
  return m;
}

// Posted (tax reference) price per realized price for a region: 1 / (1 − discount) in posted-price regions.
export function postedOverRealized(rid, y) {
  if (!POSTED.has(rid) || y < M3_FROM) return 1;
  const d = realizedDiscount(y);
  return d > 0 && d < 1 ? 1 / (1 - d) : 1;
}
